import { useLocation, useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState, type JSX } from 'react';
import backButtonDark from '@/assets/svg/static/back-button-dark.svg';
import {
  DoubleColumnProductList,
  FilterBar,
  type PriceFilterTags,
} from '@/components/features';
import type { FilterTags } from '@/components/features/FilterBar/FilterBar.types';
import { PRICE_FILTER_MAX, PRICE_RANGES } from '@/constants/priceFilterRanges';
import { fetchSimilarProductsByText } from '@/services/slaydAPI';
import type { Product } from '@/types/Product.types';
import { getMinMaxPriceRange } from '@/utils/formatting';
import styles from './SearchPage.module.css';
import type { SearchPageProps } from './SearchPage.types';

const MIN_PRICE_URL_QUERY = 'min';
const MAX_PRICE_URL_QUERY = 'max';
const SALE_URL_QUERY = 'sale';

const SearchPage = ({ onProductClick }: SearchPageProps): JSX.Element => {
  const [products, setProducts] = useState<Product[]>([]);
  const [query, setQuery] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [priceFiltersToDisplay, setPriceFiltersToDisplay] =
    useState<PriceFilterTags[]>(PRICE_RANGES);
  const [quickFilters, setQuickFilters] = useState<FilterTags[]>([]);
  const [onSale, setOnSale] = useState<boolean>(false);

  const fetchingDataRef = useRef<boolean>(false);
  const pageNumRef = useRef<number>(0);
  const saleRef = useRef<boolean>(false);

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const createCurationFlag = params.has('createCurations');
  const navigate = useNavigate();

  // setting price filters from url
  const handlePriceFiltersFromUrl = useCallback(
    (minPrice: string | null, maxPrice: string | null) => {
      if (!minPrice && !maxPrice) return;
      const minPriceNum = minPrice ? Number(minPrice) : 0;
      const maxPriceNum = maxPrice ? Number(maxPrice) : PRICE_FILTER_MAX;

      if (Number.isNaN(minPriceNum) || Number.isNaN(maxPriceNum)) return;

      const filterTags = PRICE_RANGES.map((range) => ({
        ...range,
        isSelected: range.min >= minPriceNum && range.max <= maxPriceNum,
      }));

      setPriceFiltersToDisplay((prev) => {
        const hasChanged = filterTags.some(
          (filter, index) => filter.isSelected !== prev[index].isSelected
        );
        return hasChanged ? filterTags : prev;
      });
    },
    []
  );

  // setting price filters and sale from url
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const newQuery = searchParams.get('query');

    // If query has changed, clear price filter URL parameters
    if (query !== newQuery && newQuery) {
      const cleanSearchParams = new URLSearchParams(location.search);
      cleanSearchParams.delete(MIN_PRICE_URL_QUERY);
      cleanSearchParams.delete(MAX_PRICE_URL_QUERY);
      navigate(`${location.pathname}?${cleanSearchParams.toString()}`, {
        replace: true,
      });
    }

    setQuery(newQuery);
    setQuery(searchParams.get('query'));
    setOnSale(searchParams.get(SALE_URL_QUERY) === 'true');
    saleRef.current = searchParams.get(SALE_URL_QUERY) === 'true';

    if (
      searchParams.has(MIN_PRICE_URL_QUERY) ||
      searchParams.has(MAX_PRICE_URL_QUERY)
    ) {
      handlePriceFiltersFromUrl(
        searchParams.get(MIN_PRICE_URL_QUERY),
        searchParams.get(MAX_PRICE_URL_QUERY)
      );
    }
  }, [
    location.search,
    handlePriceFiltersFromUrl,
    query,
    navigate,
    location.pathname,
  ]);

  // function to fetch product data
  const fetchProductData = useCallback(
    async (
      text: string,
      minPrice?: number,
      maxPrice?: number,
      pageNum: number = 1,
      sale?: boolean
    ) => {
      if (!text || fetchingDataRef.current) return;
      try {
        fetchingDataRef.current = true;
        setIsLoading(true);
        const productData = await fetchSimilarProductsByText({
          text: text,
          page: pageNum,
          minPrice: minPrice,
          maxPrice: maxPrice,
          ...(sale ? { sale: true } : {}),
        });
        if (productData.products.length === 0)
          throw new Error('No products received');
        setProducts((prev) => [...prev, ...productData.products]);
        pageNumRef.current = pageNum;

        // Only set quickFilters on first page
        if (pageNum === 1 && productData.quickFilters) {
          setQuickFilters(
            productData.quickFilters.map((name) => ({
              name,
              isSelected: false,
            }))
          );
        }
      } catch (error) {
        console.error('Unable to fetch product data due to: ', error);
      } finally {
        fetchingDataRef.current = false;
        setIsLoading(false);
      }
    },
    []
  );

  // useEffect to fetch product data on query, price filter, or onSale change
  useEffect(() => {
    if (!query) return;

    // Reset products, pageNumRef, and price filters when query changes
    setProducts([]);
    setQuickFilters([]);
    pageNumRef.current = 0;
  }, [query]);

  useEffect(() => {
    if (!query) return;

    const { minPrice, maxPrice } = getMinMaxPriceRange(priceFiltersToDisplay);

    // Only fetch if we don't have any products and we're not currently fetching
    if (products.length === 0 && !fetchingDataRef.current) {
      const timeoutId = setTimeout(() => {
        const searchParams = new URLSearchParams(location.search);
        const currentQuery = searchParams.get('query');
        if (currentQuery) {
          fetchProductData(
            currentQuery,
            minPrice,
            maxPrice,
            1,
            saleRef.current
          );
        }
      }, 100); // 100ms delay

      return () => clearTimeout(timeoutId);
    }

    return;
  }, [
    query,
    priceFiltersToDisplay,
    fetchProductData,
    products.length,
    onSale,
    location.search,
  ]);

  // pagination callback
  const handleLoadMore = useCallback((): void => {
    const { minPrice, maxPrice } = getMinMaxPriceRange(priceFiltersToDisplay);
    // Add a small delay to ensure URL navigation has completed
    setTimeout(() => {
      // Use the query from URL to ensure we have the latest value
      const searchParams = new URLSearchParams(location.search);
      const currentQuery = searchParams.get('query');
      if (currentQuery)
        fetchProductData(
          currentQuery,
          minPrice,
          maxPrice,
          pageNumRef.current + 1,
          saleRef.current
        );
    }, 50); // 50ms delay for pagination
  }, [fetchProductData, priceFiltersToDisplay, location.search]);

  // on changing price filters by tags
  const handlePriceFilterChange = useCallback(
    (selectedFilters: PriceFilterTags[]) => {
      const { minPrice: minPriceOld, maxPrice: maxPriceOld } =
        getMinMaxPriceRange(priceFiltersToDisplay);
      const { minPrice: minPriceNew, maxPrice: maxPriceNew } =
        getMinMaxPriceRange(selectedFilters);
      if (minPriceOld !== minPriceNew || maxPriceOld !== maxPriceNew) {
        setProducts([]);
      }
      setPriceFiltersToDisplay(selectedFilters);
      const searchParams = new URLSearchParams(location.search);
      searchParams.set('min', minPriceNew.toString());
      searchParams.set('max', maxPriceNew.toString());
      if (onSale) searchParams.set('sale', 'true');
      else searchParams.delete('sale');
      navigate(`${location.pathname}?${searchParams.toString()}`, {
        replace: true,
      });
    },
    [
      priceFiltersToDisplay,
      location.search,
      location.pathname,
      navigate,
      onSale,
    ]
  );

  // Handle quick filter click
  const handleQuickFilterClick = useCallback(
    (filterName: string) => {
      // Reset state before navigation to prevent duplicate API calls
      setProducts([]);
      setQuickFilters([]);
      pageNumRef.current = 0;
      saleRef.current = false;
      setOnSale(false);

      const searchParams = new URLSearchParams(location.search);
      searchParams.set('query', filterName);
      // Clear sale and price filters when changing query
      searchParams.delete('sale');
      navigate(`${location.pathname}?${searchParams.toString()}`);
    },
    [location.pathname, location.search, navigate]
  );
  const handleSaleToggle = useCallback(() => {
    const newSaleState = !onSale;
    setProducts([]);
    setOnSale(newSaleState);
    saleRef.current = newSaleState;
    pageNumRef.current = 0; // Reset page number when sale state changes

    const searchParams = new URLSearchParams(location.search);
    if (newSaleState) {
      searchParams.set('sale', 'true');
    } else {
      searchParams.delete('sale');
    }
    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
  }, [location.search, location.pathname, onSale, navigate]);

  const navigateToSearchSuggestion = (): void => {
    navigate(
      `/search-suggestion?query=${query}${createCurationFlag ? '&createCurations' : ''}`
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.stickyContainer}>
        <div className={`${styles.searchContainer}`}>
          <div className={styles.backButtonContainer}>
            <img
              className={styles.backButton}
              src={backButtonDark}
              alt="Back"
              onClick={() => navigate(-1)}
            />
          </div>
          <div
            className={styles.searchBar}
            onClick={navigateToSearchSuggestion}
          >
            {query}
          </div>
        </div>
        <FilterBar
          filters={quickFilters}
          priceFilter={priceFiltersToDisplay}
          isSaleSelected={onSale}
          onFilterClick={handleQuickFilterClick}
          onPriceFilterChange={handlePriceFilterChange}
          onSaleToggle={handleSaleToggle}
        />
      </div>
      <div className={styles.scrollableContainer}>
        <DoubleColumnProductList
          className={styles.productList}
          products={products}
          isLoading={isLoading}
          hasMore={true}
          onLoadMore={handleLoadMore}
          onProductClick={onProductClick}
        />
      </div>
    </div>
  );
};

export default SearchPage;
